import { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * useWhisperRecording — records browser audio via MediaRecorder, uploads to
 * the backend Whisper endpoint, and returns the transcript.
 *
 * Two modes:
 *   - 'auto'      → caller calls startListening(); the recording auto-stops
 *                   after `autoStopMs` (default 5000ms).
 *   - 'tap-stop'  → caller calls startListening() then stopListening() to
 *                   capture longer/free-form commands.
 *
 * API mirrors useVoiceRecognition so callers can swap implementations.
 */
export function useWhisperRecording({ onResult, onError, language = 'en', prompt = '' } = {}) {
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(true);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const autoStopTimerRef = useRef(null);
  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);

  useEffect(() => { onResultRef.current = onResult; }, [onResult]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.MediaRecorder || !navigator.mediaDevices?.getUserMedia) {
      setIsSupported(false);
    }
    return () => {
      if (autoStopTimerRef.current) clearTimeout(autoStopTimerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  const cleanupStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const transcribe = async (blob) => {
    if (!blob || blob.size < 1024) {
      onErrorRef.current?.('No audio captured. Please try again.');
      return;
    }
    setIsTranscribing(true);
    try {
      const form = new FormData();
      form.append('audio', blob, 'recording.webm');
      form.append('language', language);
      if (prompt) form.append('prompt', prompt);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/matches/transcribe`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const text = res.data?.transcript || '';
      setTranscript(text);
      if (text) onResultRef.current?.(text);
      else onErrorRef.current?.('No speech detected. Please try again.');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Transcription failed';
      onErrorRef.current?.(msg);
    } finally {
      setIsTranscribing(false);
    }
  };

  const startListening = useCallback(async ({ mode = 'auto', autoStopMs = 5000 } = {}) => {
    if (isListening) return;
    setTranscript('');
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // Pick a supported mime type. Whisper accepts webm directly.
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : '';
      const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        cleanupStream();
        const blob = new Blob(chunksRef.current, { type: mime || 'audio/webm' });
        chunksRef.current = [];
        transcribe(blob);
      };

      recorder.start();
      setIsListening(true);

      if (mode === 'auto' && autoStopMs > 0) {
        autoStopTimerRef.current = setTimeout(() => {
          if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
            setIsListening(false);
          }
        }, autoStopMs);
      }
    } catch (err) {
      cleanupStream();
      setIsListening(false);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        onErrorRef.current?.('Microphone access denied. Please allow microphone permissions.');
      } else {
        onErrorRef.current?.(`Microphone error: ${err.message}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isListening, language, prompt]);

  const stopListening = useCallback(() => {
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsListening(false);
  }, []);

  return {
    isListening,
    isTranscribing,
    transcript,
    isSupported,
    startListening,
    stopListening,
  };
}
